import { Link, useNavigate } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { buildResourceInstanceUri } from "@/hooks/useRemaUriBuilder"
import { ArrowLeft, Loader2, Edit, Database, Link2, GitBranch } from "lucide-react"
import { ProfileHeader, ProfileSection } from "@/components/sensnet/ProfileLayout"

interface ResourceInstanceDetailPageProps {
  instanceNumber: number | string
}

export function ResourceInstanceDetailPage({ instanceNumber }: ResourceInstanceDetailPageProps) {
  const navigate = useNavigate()

  const { data, isLoading, isError } = useApiQuery("getResourceInstances", {})

  const instance = ((data as any)?.content?.resourceInstances || (data as any)?.resourceInstances || []).find(
    (i: any) => String(i.instanceNumber) === String(instanceNumber)
  )

  const uri = instance ? buildResourceInstanceUri({
    namespace: instance.namespace ?? 'cnx',
    tenantId: instance.tenantId ?? 997,
    category: instance.resourceCategory ?? '',
    parentInstancePaths: instance.parentInstancePaths ?? [],
    resourceType: instance.resourceTypeName ?? '',
    instanceName: instance.resourceInstanceName ?? '',
  }) : ''

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="text-muted-foreground">Loading resource instance details...</span>
        </div>
      </div>
    )
  }

  if (isError || !instance) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-sm text-muted-foreground">Resource instance not found.</p>
        <Button variant="outline" onClick={() => navigate({ to: '/rema/resource-instances' })}>Back to list</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button asChild variant="ghost" size="sm" className="gap-2 -ml-2">
          <Link to="/rema/resource-instances">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </Button>
        <Button asChild size="sm" className="gap-2">
          <Link to="/rema/resource-instances/$instanceNumber/edit" params={{ instanceNumber: String(instance.instanceNumber) }}>
            <Edit className="h-4 w-4" />
            Edit
          </Link>
        </Button>
      </div>

      <ProfileHeader
        title={instance.resourceInstanceName}
        subtitle={`Instance Number: ${instance.instanceNumber}`}
        subtitleIcon={<Database className="h-4 w-4" />}
        badge={instance.resourceCategory}
      />

      <ProfileSection title="Identity" icon={<Database className="h-4 w-4" />} variant="dark">
        <div className="space-y-3 p-5">
          <div>
            <p className="text-xs text-muted-foreground">Name</p>
            <p className="text-sm">{instance.resourceInstanceName}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Category</p>
            <p className="text-sm">{instance.resourceCategory ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Description</p>
            <p className="text-sm">{instance.resourceInstanceDescription || '—'}</p>
          </div>
        </div>
      </ProfileSection>

      <ProfileSection title="Resource URI" icon={<Link2 className="h-4 w-4" />} variant="dark">
        <div className="space-y-3 p-5">
          <div className="rounded-md border bg-muted/50 px-3 py-2">
            <code className="text-sm font-mono break-all">{uri || '—'}</code>
          </div>
        </div>
      </ProfileSection>

      <ProfileSection title="Parent Path" icon={<GitBranch className="h-4 w-4" />} variant="dark">
        <div className="space-y-3 p-5">
          {instance.parentInstancePaths?.length > 0 ? (
            <ol className="space-y-1">
              {instance.parentInstancePaths.map((p: { resourceType: string; instanceId: string }, i: number) => (
                <li key={i} className="text-sm flex gap-2">
                  <span className="text-muted-foreground">{i + 1}.</span>
                  <span className="font-medium">{p.resourceType}</span>
                  <span className="text-muted-foreground">→</span>
                  <span>{p.instanceId}</span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-sm text-muted-foreground">No parent path</p>
          )}
        </div>
      </ProfileSection>
    </div>
  )
}
